import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  DrawingUtils,
  FaceLandmarker,
  FaceLandmarkerResult,
  FilesetResolver,
} from '@mediapipe/tasks-vision';
import {
  MediaPermissionsError,
  MediaPermissionsErrorType,
  requestMediaPermissions,
} from 'mic-check';
import { FaceStabilizationService } from '../services/face-stabilization.service';
import {
  MessageHandlerService,
  CameraMessage,
  MessageType,
} from '../services/message-handler.service';
import { MatIconModule } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { SessionService } from '../services/session.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { ErrorReportingService } from '../services/error-reporting.service';
import { PlatformService } from '../services/platform.service';
import {
  StillFrameSelectorService,
  laplacianVariance,
} from '../services/still-frame-selector.service';

// True when every landmark has finite x/y (and z when present). Some GPU
// delegates (seen on a Galaxy S25 Ultra, Chrome 152) emit a whole frame of
// NaN landmarks: NaN passes every `<`/`>` gate check as "valid", draws no
// mesh, turns the crop into a NaN-sized (= 0px) canvas, and poisons
// MediaPipe's own VIDEO-mode tracking so it never finds the face again.
export function landmarksFinite(
  landmarks: { x: number; y: number; z?: number }[] | undefined | null
): boolean {
  if (!landmarks?.length) return false;
  for (const p of landmarks) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return false;
    if (p.z !== undefined && !Number.isFinite(p.z)) return false;
  }
  return true;
}

@Component({
  selector: 'app-camera',
  standalone: true,
  imports: [MatIconModule, NgIf, TranslatePipe],
  templateUrl: './camera.component.html',
  styleUrl: './camera.component.scss',
})
export class CameraComponent {
  @ViewChild('webcam') private video: ElementRef;
  @ViewChild('output_canvas') private output_canvas: ElementRef;
  @ViewChild('fileInput') private fileInput: ElementRef;
  @Output() imageCaptured = new EventEmitter<void>();

  noCameraReason = null;
  faceLandmarker: FaceLandmarker;
  videoWidth = 1920;
  captureImageOnOrientationInProgress = false;

  private lastVideoTime: number;
  private drawingUtils: DrawingUtils;

  @Input() capturedImages: any;

  @Input() readyToStart!: boolean;

  demo = {
    cover: new Image(),
  };

  blinkCounter = 0;
  eyeClosed = false;
  faceDirection: {
    forehead?: number;
    chin?: number;
    pitch?: number;
    roll?: number;
    yaw?: number;
  } = {
    pitch: 0, // Up-Down
    yaw: 0, // Left-Right
    roll: 0, // Side Tilt
  };
  faceFoundLandMarks: any;
  loaded = false;
  private output_ctx: any;
  error: CameraMessage;

  private eyeBlinkLeft: any;
  private eyeBlinkRight: any;
  // Scratch canvases for the per-frame eye-patch sharpness probe (reused).
  // Detection runs on a downscaled copy of the frame: the landmarker only
  // needs ~256px of face, but uploading a 2560x2560 video texture per frame
  // is what held a Galaxy S25 Ultra at ~14 fps (mesh visibly lagging the
  // face). Landmarks are normalized, so they map 1:1 onto the full-res video
  // the upload is cut from.
  // Overlay (mesh + guides) canvas long-side cap; drawn scaled to the video box.
  private readonly OVERLAY_MAX_SIDE = 1080;
  private detectCanvas: HTMLCanvasElement | null = null;
  private detectCtx: CanvasRenderingContext2D | null = null;
  private readonly DETECT_MAX_SIDE = 640;
  // ONE video->canvas copy per detection frame. On some phones (Galaxy S25
  // Ultra) each copy from the camera element costs tens of ms whatever the
  // destination size, and the hold phase used to make four of them per frame
  // (detection, brightness, two eye patches) plus the candidate: ~2 fps right
  // where she needs it smooth. Now: the grab is small (DETECT_MAX_SIDE) while
  // red, and large enough for a 1080px candidate while green; detection,
  // brightness, sharpness and the candidate all read from that grab.
  private frameCanvas: HTMLCanvasElement | null = null;
  private frameCtx: CanvasRenderingContext2D | null = null;
  private frameScale = 1; // grab px per video px
  private frameIsFull = false; // large enough to cut a candidate from
  private wantFullGrab = false; // set by the gate: position valid last frame
  private lastCropSw = 0; // video px, from the last gate evaluation
  // Per-section timings, reported every PERF_REPORT_MS as camera.perf so lag
  // is diagnosable from telemetry on any device.
  private perf: Record<string, { n: number; ms: number }> = {};
  private poseSamples: number[][] = [];
  private lastPoseSampleAt = 0;
  private perfReportedAt = 0;
  private readonly PERF_REPORT_MS = 10000;
  private perfAdd(k: string, ms: number) {
    const a = this.perf[k] || (this.perf[k] = { n: 0, ms: 0 });
    a.n++;
    a.ms += ms;
  }
  private perfFlush() {
    const now = performance.now();
    if (!this.perfReportedAt) this.perfReportedAt = now;
    if (now - this.perfReportedAt < this.PERF_REPORT_MS) return;
    const out: Record<string, number | string> = {};
    for (const [k, v] of Object.entries(this.perf)) {
      out[k] = `${v.n}x${(v.ms / v.n).toFixed(1)}`; // count x avg ms
    }
    out['delegate'] = CameraComponent.landmarkerDelegate;
    out['pose'] = JSON.stringify(this.poseSamples); // [legacyRoll, legacyYaw, pitch3d, yaw3d, roll3d]
    this.poseSamples = [];
    this.reporter.logEvent('camera.perf', out);
    this.perf = {};
    this.perfReportedAt = now;
  }
  private sharpCanvas: HTMLCanvasElement | null = null;
  private sharpCtx: CanvasRenderingContext2D | null = null;
  private readonly SHARP_PATCH_MAX_W = 192; // px, patch drawn 1:1 up to this
  private readonly BLINK_THRESHOLD = 0.5;
  // Candidate frames are drawn at most this often (~15 fps): on a 2560px
  // stream every sample is a multi-megapixel draw, and iOS Safari also has a
  // hard canvas-memory budget (blowing it blanks canvases, mesh included).
  private readonly SAMPLE_MIN_INTERVAL_MS = 100;
  private lastSampleAt = 0;
  // Candidate canvases are capped below the upload cap (NORM_MAX_SHORT_SIDE):
  // still well above the 1080 HD floor, a quarter of the memory of 2048x2560.
  private readonly CANDIDATE_MAX_SHORT_SIDE = 1080; // = the API's HD floor

  private static faceLandmarker: FaceLandmarker | null = null;
  // Declared BEFORE landmarkerDelegate: static initializers run in order, and
  // rememberedDelegate() reads this key (it silently read undefined before,
  // so every visit started on the broken GPU delegate again).
  private static readonly DELEGATE_KEY = 'camera.landmarkerDelegate';
  private static landmarkerDelegate: 'GPU' | 'CPU' = CameraComponent.rememberedDelegate();
  private static warmedUp = false;
  private static preloading: Promise<void> | null = null;

  // A device whose GPU delegate produced NaN / no faces is remembered, so the
  // next visit starts on the CPU delegate immediately instead of spending
  // ~10s (two recovery rounds) rediscovering it.
  private static rememberedDelegate(): 'GPU' | 'CPU' {
    try {
      return localStorage.getItem(CameraComponent.DELEGATE_KEY) === 'CPU' ? 'CPU' : 'GPU';
    } catch {
      return 'GPU';
    }
  }
  private static rememberDelegate(d: 'GPU' | 'CPU') {
    try {
      localStorage.setItem(CameraComponent.DELEGATE_KEY, d);
    } catch {
      // storage may be unavailable; the session still uses the right delegate
    }
  }

  // Load + warm the landmarker BEFORE the camera step (the host calls this
  // while she reads the consent / photo-prep dialogs), so the camera opens
  // with its mesh instead of seconds of "camera but no mesh".
  static preload(reporter?: ErrorReportingService): Promise<void> {
    if (CameraComponent.preloading) return CameraComponent.preloading;
    CameraComponent.preloading = (async () => {
      try {
        const tmp = Object.create(CameraComponent.prototype) as CameraComponent;
        (tmp as any).reporter = reporter || { logEvent: () => {}, report: () => {} };
        await tmp.createFaceLandmarker();
        await tmp.warmUpLandmarker();
      } catch (e) {
        reporter?.report(e, 'camera');
      } finally {
        CameraComponent.preloading = null;
      }
    })();
    return CameraComponent.preloading;
  }
  // True while the shared landmarker is temporarily in IMAGE mode to detect a
  // static uploaded photo; pauses the live VIDEO detection loop meanwhile.
  private isDetectingImage = false;

  // ── Detector self-healing ─────────────────────────────────────────────────
  // (1) NaN frames (see landmarksFinite): drop the frame, reset the graph, and
  //     after NAN_CPU_FALLBACK_AFTER of them rebuild the landmarker on the CPU
  //     delegate (slower, numerically sane). (2) Stall watchdog: a face that
  //     was tracked and then vanishes for DETECTOR_STALL_MS while the video
  //     keeps playing gets the graph reset too, so the gate never freezes on
  //     its last message. Both are logged so we can see them per device.
  private nanFrames = 0;
  private readonly NAN_CPU_FALLBACK_AFTER = 1; // a NaN frame is never legit
  private readonly DETECTOR_STALL_MS = 3000;
  private readonly RECOVERY_COOLDOWN_MS = 4000;
  private stallRecoveries = 0; // consecutive, reset when a face is seen
  private lastFaceAt = 0;
  private lastDetectTs = 0;
  private detectorPoisoned = false;
  private faceEverSeen = false;
  private loadedAt = 0; // camera loaded; a graph that never finds a face gets reset too
  private readonly NEVER_SEEN_MS = 6000;
  private lastRecoveryAt = 0;
  private recovering = false;

  private readonly FOCUS_DELAY = 1000; // Time to wait for camera focus in ms
  private readonly MIN_BRIGHTNESS_THRESHOLD = 50; // Minimum average brightness (0-255)
  // Desktop-webcam relaxations. Mobile (native app + portrait mobile-web) keeps
  // the strict values; only landscape web (= a desktop webcam) loosens the
  // borderline gates so the user can actually hold all-green long enough to
  // capture. Set once video dimensions are known (setupWebcam). See the
  // position-box widening in FaceStabilizationService for the matching change.
  private relaxedFraming = false;
  private readonly RELAXED_MIN_BRIGHTNESS = 42; // desktop webcams underexpose vs phones
  private readonly RELAXED_MIN_NATIVE_SHORT_SIDE = 480; // server upscales 480 -> 1080 (~2.25x)

  private get effMinBrightness(): number {
    return this.relaxedFraming
      ? this.RELAXED_MIN_BRIGHTNESS
      : this.MIN_BRIGHTNESS_THRESHOLD;
  }
  private get effMinNativeShortSide(): number {
    return this.relaxedFraming
      ? this.RELAXED_MIN_NATIVE_SHORT_SIDE
      : this.NORM_MIN_NATIVE_SHORT_SIDE;
  }

  // ── Face normalization: single source of truth for framing ────────────────
  // Every captured image is cropped to a FIXED portrait aspect, face centered,
  // face occupying a FIXED fraction of the crop. Output is therefore identical
  // across cameras/resolutions/aspect ratios and always satisfies the skin API
  // (PerfectCorp needs face width > 60% of image width; 60-80% is optimal).
  // This replaces all the colliding per-frame size rules with one distance axis:
  // too-low-res camera / move closer / move back. Resolution is finalized on the
  // server (upscales the native crop to the 1080 HD minimum).
  private readonly NORM_ASPECT_W = 4; // output width : height = 4 : 5 (portrait)
  private readonly NORM_ASPECT_H = 5;
  private readonly NORM_FACE_WIDTH_FRAC = 0.7; // face width = 70% of crop width
  private readonly NORM_FACE_HEIGHT_FRAC = 0.8; // face height <= 80% of crop height
  // Client never upscales (it sends native crop pixels). The crop short side
  // must be >= NORM_MIN_NATIVE_SHORT_SIDE; the SERVER then upscales to the
  // PerfectCorp HD min of 1080 with sharp (so 720p cams are usable, bounded to
  // ~2x upscale). A frame that can't even reach this floor full-frame is a
  // genuinely too-low-res camera -> distinct reject message.
  private readonly NORM_MIN_NATIVE_SHORT_SIDE = 540; // server upscales 540 -> 1080
  private readonly NORM_MAX_SHORT_SIDE = 2048; // keep long side (x5/4) <= 2560 cap
  // Face nearly fills the frame on either axis -> it would be cropped at the
  // edges; the symmetric "move back" end of the single distance rule.
  private readonly NORM_FACE_FILL_MAX = 0.92;
  private cameraStartTime: number;

  // ── Never-stuck fallback ──────────────────────────────────────────────────
  // The gate is auto-shutter with no manual escape: if a customer's environment
  // can't satisfy every check (bad lighting, small/low-res face, position),
  // capture never fires and they cannot proceed. So after a grace period we
  // surface a manual "Continue anyway" button. The photo is ONLY taken by the
  // green gate or by that explicit tap: there is no timed auto-capture (it
  // used to fire after 15s and took red, badly framed shots). The server
  // remediates a forced image (upscale / crop / downscale) and records the
  // attempt so we can see + improve every stuck case.
  // "Continue anyway" appears after RED_MS of continuous red (any green frame
  // resets that clock and hides it again), or after TOTAL_MS without a shot
  // no matter what (green that never completes a hold).
  private readonly MANUAL_BTN_RED_MS = 15000;
  private readonly MANUAL_BTN_TOTAL_MS = 50000;
  private redSince = 0;
  private readySince = 0; // when the live gate first started evaluating
  private forcing = false;
  showContinueAnyway = false; // bound in template
  // Latest client-side guidance reason (stable key, not translated text) — used
  // for debounced telemetry + passed to the server per capture attempt.
  private currentReason = '';
  private lastLoggedReason = '';

  // Records the current guidance reason and logs it once per change (the gate
  // runs every frame, so we must debounce or it floods the telemetry budget).
  private setReason(reason: string) {
    this.currentReason = reason;
    if (reason && reason !== this.lastLoggedReason) {
      this.lastLoggedReason = reason;
      this.reporter.logEvent('camera.guidance', { reason });
    }
  }

  // i18n key for the "this may affect your results — you can continue anyway"
  // warning shown next to the escape hatch, specific to why capture is stuck.
  get continueWarningKey(): string {
    const map: Record<string, string> = {
      lightingTooDark: 'cameraErrors.affect.lighting',
      moveCloser: 'cameraErrors.affect.tooFar',
      moveBack: 'cameraErrors.affect.tooClose',
      cameraResolutionTooLow: 'cameraErrors.affect.resolution',
      faceNotFound: 'cameraErrors.affect.noFace',
      position: 'cameraErrors.affect.position',
      holdStill: 'cameraErrors.affect.movement',
      movement: 'cameraErrors.affect.movement',
    };
    return map[this.currentReason] || 'cameraErrors.affect.generic';
  }

  constructor(
    private faceStabilization: FaceStabilizationService,
    private messageHandler: MessageHandlerService,
    public sessionService: SessionService,
    public translationService: TranslationService,
    private reporter: ErrorReportingService,
    private platform: PlatformService,
    private frameSelector: StillFrameSelectorService
  ) {
    this.demo.cover.src = 'assets/cover.png';
  }

  // Backgrounding the tab / switching apps freezes rAF and often leaves the
  // camera track muted or the detector state stale; coming back looked
  // "stuck". On return, rebuild the whole live pipeline: new stream, fresh
  // hold/selector/escape-hatch clocks, fresh detector graph.
  private onVisibility = () => {
    if (document.visibilityState !== 'visible') {
      this.hiddenAt = performance.now();
      return;
    }
    if (this.capturedImages?.front) return;
    // Android does not always deliver the 'hidden' half of an app switch:
    // re-init on EVERY return to the foreground (rate limited).
    const awayMs = this.hiddenAt ? Math.round(performance.now() - this.hiddenAt) : null;
    this.hiddenAt = 0;
    if (performance.now() - this.lastReinitAt < 2000) return;
    this.reporter.logEvent('camera.resume', { awayMs });
    this.reinitLive();
  };
  private hiddenAt = 0;
  private lastReinitAt = 0;
  // Heartbeat: the rAF loop and the video are checked every second. A loop
  // that stopped ticking is restarted; a video whose time stopped advancing
  // (track paused by the OS after an app switch) gets the camera re-opened.
  private lastLoopAt = 0;
  private lastVideoProgressAt = 0;
  private lastVideoT = -1;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private startHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = setInterval(() => {
      if (document.visibilityState !== 'visible' || this.capturedImages?.front) return;
      if (!this.loaded) return;
      const now = performance.now();
      const v = this.video?.nativeElement as HTMLVideoElement | undefined;
      if (v) {
        if (v.currentTime !== this.lastVideoT) {
          this.lastVideoT = v.currentTime;
          this.lastVideoProgressAt = now;
        } else if (this.lastVideoProgressAt && now - this.lastVideoProgressAt > 3000) {
          this.reporter.logEvent('camera.videoStalled', { stalledMs: Math.round(now - this.lastVideoProgressAt) });
          this.lastVideoProgressAt = now;
          this.reinitLive();
          return;
        }
      }
      if (this.lastLoopAt && now - this.lastLoopAt > 2000) {
        this.reporter.logEvent('camera.loopDead', { deadMs: Math.round(now - this.lastLoopAt) });
        this.lastLoopAt = now;
        this.isDetectingImage = false;
        this.recovering = false;
        this.loopToken++;
        this.predictWebcam(this.loopToken);
      }
    }, 1000);
  }
  private reinitLive() {
    this.lastReinitAt = performance.now();
    // Stop the running loop before touching the stream: its next iteration
    // must not feed the landmarker a dead/0x0 video.
    this.loopToken++;
    this.lastVideoTime = -1;
    try {
      const old = this.video?.nativeElement?.srcObject as MediaStream | null;
      old?.getVideoTracks()?.forEach((t) => t.stop());
      if (this.video?.nativeElement) this.video.nativeElement.srcObject = null;
    } catch {
      // best effort
    }
    this.loaded = false;
    this.lastVideoTime = -1;
    this.readySince = 0;
    this.redSince = 0;
    this.showContinueAnyway = false;
    this.faceEverSeen = false;
    this.lastFaceAt = 0;
    this.stallRecoveries = 0;
    this.faceStabilization.resetStability();
    this.frameSelector.reset();
    // Fresh landmarker too (destroy + re-create): the loop is already
    // stopped (token bumped above), so nothing can feed the graph while it
    // is rebuilt; the poisoned flag keeps the new loop off it until then.
    this.detectorPoisoned = true;
    this.recoverDetector('nan');
    this.loadedAt = performance.now();
    this.enableCam();
  }

  init = async () => {
    if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname)) {
      (window as any).__cam = this; // dev-only: live inspection over CDP
    }
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.startHeartbeat();
    // The stabilization service + frame selector are singletons: after a shot
    // (then Retake) the hold clock would still read "complete" and the new
    // camera would capture on its very first frame, with a single sample.
    this.faceStabilization.resetStability();
    this.frameSelector.reset();
    await this.createFaceLandmarker();
    await this.warmUpLandmarker();
    this.output_ctx = this.output_canvas.nativeElement.getContext('2d');
    this.lastVideoTime = -1;
    this.drawingUtils = new DrawingUtils(this.output_ctx);
    this.enableCam();
  };

  captureImageOnOrientation = async () => {
    if (!this.readyToStart) {
      console.log('not yet');
      return;
    }

    this.messageHandler.setHasPositionError(false);

    // Track how long we've been trying without a capture, and arm the escape
    // hatches so a customer is never permanently stuck on the quality gate.
    if (!this.readySince) this.readySince = Date.now();
    if (!this.capturedImages?.front && !this.forcing) {
      const now = Date.now();
      if (!this.redSince) this.redSince = now;
      const redMs = now - this.redSince;
      const totalMs = now - this.readySince;
      // Never while she is green (last frame's verdict): a success must not
      // carry a "may affect your results" warning.
      this.showContinueAnyway =
        !this.wantFullGrab &&
        (redMs >= this.MANUAL_BTN_RED_MS || totalMs >= this.MANUAL_BTN_TOTAL_MS);
    }

    const isBrightEnough = this.videoBrightnessOk();
    if (!isBrightEnough) {
      this.setReason('lightingTooDark');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.lightingTooDark'),
        1
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }

    if (!this.faceFoundLandMarks) {
      this.setReason('faceNotFound');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.faceNotFound'),
        5
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }

    // Single distance axis: camera capable? face big enough? not too close?
    // Centering/aspect/face-size are all handled deterministically by the crop;
    // the server upscales the native crop to the 1080 HD minimum.
    const _crop = this.computeFaceCrop(
      this.video.nativeElement.videoWidth,
      this.video.nativeElement.videoHeight
    );
    if (_crop && !_crop.cameraCapable) {
      this.setReason('cameraResolutionTooLow');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.cameraResolutionTooLow'),
        0
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }
    if (!_crop || _crop.sw < this.effMinNativeShortSide) {
      this.setReason('moveCloser');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.moveCloser'),
        3
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }
    if (_crop.tooClose) {
      this.setReason('moveBack');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.moveBack'),
        3
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }

    if (Date.now() - this.cameraStartTime < this.FOCUS_DELAY) {
      this.setReason('waitingForFocus');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.waitingForFocus'),
        5
      );
      this.messageHandler.setHasPositionError(true);
      return true;
    }

    this.lastCropSw = _crop.sw;
    const positionCheck = this.faceStabilization.isPositionValid(
      this.faceDirection,
      this.faceFoundLandMarks
    );
    this.wantFullGrab = positionCheck.isValid;
    this.axes = this.faceStabilization.positionAxes(
      this.faceDirection,
      this.faceFoundLandMarks
    );
    if (positionCheck.isValid) {
      // Green reached: the "stuck in red" clock restarts (the total clock
      // does not).
      this.redSince = 0;
      this.showContinueAnyway = false;
    }
    if (!positionCheck.isValid) {
      this.setReason('position');
      this.error = this.messageHandler.updateMessage(positionCheck.error, 3);
      this.messageHandler.setHasPositionError(true);
      this.faceStabilization.resetStability();

      return true;
    }

    // Position is valid (green): sample this frame as a capture candidate.
    // Every green frame is scored for stillness/sharpness so the final upload
    // is the stillest moment of the hold, not whatever the shutter instant hit.
    this.sampleFrameForCapture(_crop);

    const isStable = this.faceStabilization.checkStability(
      this.faceFoundLandMarks,
      this.faceDirection
    );
    if (!isStable) {
      // isHolding (not elapsed > 0): the clock starts at 0ms on the first
      // stable frame, and reading that as "movement" flashed a red message
      // at the start of every hold.
      if (this.faceStabilization.isHolding()) {
        this.setReason('holdStill');
        const countdownMsg = this.messageHandler.getCountdownMessage(
          this.faceStabilization.getHoldElapsedMs(),
          this.faceStabilization.getHoldRequiredMs()
        );
        this.error = this.messageHandler.updateMessage(
          countdownMsg.text,
          1,
          countdownMsg.type
        );
      } else {
        this.setReason('movement');
        const movementMsg = this.messageHandler.getMovementDetectedMessage();
        this.error = this.messageHandler.updateMessage(
          movementMsg.text,
          2,
          movementMsg.type
        );
      }
      return true;
    }

    if (!this.capturedImages.front) {
      this.setReason('ok');
      this.showContinueAnyway = false;
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.capturing'),
        4,
        MessageType.COUNTDOWN
      );
      await this.captureImage('front');
    }
  };

  ngAfterViewInit() {
    this.init();
  }

  createFaceLandmarker = async (delegate?: 'GPU' | 'CPU') => {
    const wanted = delegate || CameraComponent.landmarkerDelegate;
    if (
      CameraComponent.faceLandmarker &&
      CameraComponent.landmarkerDelegate === wanted
    ) {
      this.faceLandmarker = CameraComponent.faceLandmarker;
      return;
    }

    this.reporter.logEvent('camera.landmarker.load.start', { delegate: wanted });
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.15/wasm'
      );

      const fresh = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
          delegate: wanted,
        },
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        runningMode: 'VIDEO',
        numFaces: 1,
      });
      // Swap after the new one is ready; close the old one off the hot path.
      const old = CameraComponent.faceLandmarker;
      CameraComponent.faceLandmarker = fresh;
      CameraComponent.landmarkerDelegate = wanted;
      CameraComponent.warmedUp = false;
      this.faceLandmarker = fresh;
      if (old && old !== fresh) {
        try {
          old.close();
        } catch {
          // best effort
        }
      }
      this.reporter.logEvent('camera.landmarker.load.ok', { delegate: wanted });
    } catch (e) {
      // CDN/WASM/GPU init failure — face detection can't run; surface it.
      this.reporter.report(e, 'camera');
      throw e;
    }
  };

  enableCam = async () => {
    // Request the front camera at high resolution but DON'T hard-pin a minimum.
    // iPad / iOS WKWebView rejects strict resolution constraints (min width/height)
    // with OverconstrainedError, which previously surfaced as an error message the
    // instant the camera opened — the App Store 2.1 rejection (iPad Air M3 / iPadOS
    // 26.5: "Error message when we tried to open camera"). `ideal` lets the browser
    // pick the best available stream; we fall back to an unconstrained front-camera
    // request so the camera always opens even when ideal can't be satisfied.
    const idealConstraints = {
      video: {
        width: { ideal: 2560 },
        height: { ideal: 2560 },
        facingMode: 'user',
      },
    };

    this.reporter.logEvent('camera.open');
    let stream: MediaStream | null = null;
    try {
      console.log('CameraComponent: Requesting front camera (ideal up to 2560px)');
      stream = await this.withTimeout(
        navigator.mediaDevices.getUserMedia(idealConstraints),
        this.GUM_TIMEOUT_MS,
        'getUserMedia(ideal) timed out'
      );
    } catch (e) {
      console.warn(
        'CameraComponent: ideal-resolution request failed, retrying without resolution constraints:',
        e
      );
      this.reporter.logEvent('camera.open.fallback', {
        reason: (e as any)?.name || 'unknown',
      });
      try {
        // No resolution constraints — opens the front camera at whatever resolution
        // the device / WKWebView supports.
        stream = await this.withTimeout(
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } }),
          this.GUM_TIMEOUT_MS,
          'getUserMedia(fallback) timed out'
        );
      } catch (e2) {
        console.error('CameraComponent: front-camera request failed, diagnosing permission:', e2);
        this.reporter.report(e2, 'camera');
        try {
          await requestMediaPermissions({ video: true, audio: false });
        } catch (err: MediaPermissionsError | any) {
          const { type } = err;
          if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
            this.noCameraReason = this.translationService.translate(
              'cameraErrors.browserNoPermission'
            );
          } else if (type === MediaPermissionsErrorType.UserPermissionDenied) {
            this.noCameraReason = this.translationService.translate(
              'cameraErrors.userDeniedPermission'
            );
          } else if (
            type === MediaPermissionsErrorType.CouldNotStartVideoSource
          ) {
            this.noCameraReason = this.translationService.translate(
              'cameraErrors.cameraInUse'
            );
          } else {
            this.noCameraReason = this.translationService.translate(
              'cameraErrors.cameraCannotLoad'
            );
          }
          this.reporter.logEvent('camera.permission.denied', {
            type: type || 'unknown',
          });
        }
        return;
      }
    }

    this.video.nativeElement.srcObject = stream;
    console.log('CameraComponent: Camera initialized successfully');
    this.noCameraReason = null;
    this.armNoFramesWatchdog(stream);
    if (this.loadedListener) {
      this.video.nativeElement.removeEventListener('loadeddata', this.loadedListener);
    }
    this.loadedListener = async () => {
      clearTimeout(this.noFramesTimer);
      this.cameraAttempts = 0;
      // Log actual video dimensions for debugging
      const video = this.video.nativeElement;
      console.log(
        `CameraComponent: Video stream loaded - Actual dimensions: ${video.videoWidth}x${video.videoHeight}`
      );

      // Check if dimensions meet YouCam HD requirements (1080px on short side, max 2560px on long side)
      const shortSide = Math.min(video.videoWidth, video.videoHeight);
      const longSide = Math.max(video.videoWidth, video.videoHeight);
      console.log(
        `CameraComponent: Resolution: ${video.videoWidth}x${video.videoHeight} (short: ${shortSide}px, long: ${longSide}px)`
      );
      console.log(
        `CameraComponent: YouCam HD requirements - Min short side: 1080px, Max long side: 2560px`
      );

      // The image we upload is a fixed NORM_ASPECT portrait crop. The largest
      // such crop this stream can yield (full-frame fit) must be >= 1080px on
      // its short side, with NO upscaling. If not, the camera is too low-res.
      const aspect = this.NORM_ASPECT_W / this.NORM_ASPECT_H;
      const maxCropShort = Math.min(
        video.videoWidth,
        video.videoHeight * aspect
      );

      if (maxCropShort < this.NORM_MIN_NATIVE_SHORT_SIDE) {
        console.warn(
          `CameraComponent: WARNING - ${video.videoWidth}x${video.videoHeight} cannot produce a ${this.NORM_MIN_NATIVE_SHORT_SIDE}px ${this.NORM_ASPECT_W}:${this.NORM_ASPECT_H} crop (max ${Math.round(maxCropShort)}px) - too low-res`
        );
        this.reporter.report(
          `camera resolution too low: ${video.videoWidth}x${video.videoHeight} (maxCropShort ${Math.round(maxCropShort)} < ${this.NORM_MIN_NATIVE_SHORT_SIDE})`,
          'camera'
        );
        this.error = this.messageHandler.updateMessage(
          this.translationService.translate('cameraErrors.cameraResolutionTooLow'),
          0 // Persistent error
        );
        this.loaded = false;
        return;
      }

      console.log(
        `CameraComponent: Resolution OK - can crop up to ${Math.round(maxCropShort)}px short side ✓`
      );
      this.reporter.logEvent('camera.ready', {
        w: video.videoWidth,
        h: video.videoHeight,
      });
      this.error = null;

      // Created in VIDEO mode and warmed up already; rebuilding the graph here
      // (setOptions) threw that away and cost seconds of "camera but no mesh".
      this.loaded = true;
      this.loadedAt = performance.now();
      this.loopToken++;
      this.predictWebcam(this.loopToken);
    };
    this.video.nativeElement.addEventListener('loadeddata', this.loadedListener);
  };

  // Android can leave getUserMedia pending forever while the camera HAL is
  // busy (another tab / app), and it can also hand out a stream that never
  // delivers a frame (track muted). Both used to mean a loader that never
  // ends. The request gets a timeout, and a granted stream has NO_FRAMES_MS to
  // fire loadeddata; then we retry the camera once and finally show the
  // "camera in use" screen (the host offers Retry there).
  private readonly GUM_TIMEOUT_MS = 8000;
  private readonly NO_FRAMES_MS = 5000;
  private noFramesTimer: ReturnType<typeof setTimeout> | null = null;
  private cameraAttempts = 0;
  private loadedListener: (() => Promise<void>) | null = null;
  private withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error(what)), ms);
      p.then(
        (v) => {
          clearTimeout(t);
          resolve(v);
        },
        (e) => {
          clearTimeout(t);
          reject(e);
        }
      );
    });
  }
  private armNoFramesWatchdog(stream: MediaStream) {
    if (this.noFramesTimer) clearTimeout(this.noFramesTimer);
    this.noFramesTimer = setTimeout(() => {
      if (this.loaded) return;
      const track = stream.getVideoTracks()[0];
      this.reporter.logEvent('camera.noFrames', {
        attempt: this.cameraAttempts,
        muted: track?.muted ?? null,
        state: track?.readyState ?? null,
      });
      stream.getVideoTracks().forEach((t) => t.stop());
      if (this.video?.nativeElement) this.video.nativeElement.srcObject = null;
      if (this.cameraAttempts < 1) {
        this.cameraAttempts++;
        this.enableCam();
      } else {
        this.cameraAttempts = 0;
        this.noCameraReason = this.translationService.translate(
          'cameraErrors.cameraInUse'
        );
      }
    }, this.NO_FRAMES_MS);
  }

  setupWebcam = async () => {
    const videoElement = this.video.nativeElement;
    const canvasElement = this.output_canvas.nativeElement;

    // Use video's raw dimensions for canvas (this ensures proper MediaPipe coordinate mapping)
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    // Desktop webcam = web platform delivering a landscape stream. Phones (native
    // app + portrait mobile-web) stay on the strict gates that already work; only
    // here do we loosen the framing envelope. Recomputed each setup (stream can
    // change), and pushed to the stabilization service which owns the position box.
    const isLandscape = videoWidth > videoHeight;
    this.relaxedFraming = this.platform.isWeb && isLandscape;
    this.faceStabilization.setRelaxedFraming(this.relaxedFraming);

    // Overlay canvas: same aspect as the video, but bounded. It used to be the
    // full video size and re-assigned EVERY frame (a 2560x2560 canvas was
    // re-allocated + cleared + covered 15x per second: 60% of the main thread
    // on a Galaxy S25 Ultra). Landmarks are normalized, so the mesh scales.
    const longSide = Math.max(videoWidth, videoHeight);
    const k = longSide > this.OVERLAY_MAX_SIDE ? this.OVERLAY_MAX_SIDE / longSide : 1;
    const cw = Math.max(1, Math.round(videoWidth * k));
    const ch = Math.max(1, Math.round(videoHeight * k));
    if (canvasElement.width !== cw || canvasElement.height !== ch) {
      canvasElement.width = cw;
      canvasElement.height = ch;
      console.log(
        `CameraComponent: overlay canvas ${cw}x${ch} for video ${videoWidth}x${videoHeight}`
      );
    } else {
      this.output_ctx.clearRect(0, 0, cw, ch);
    }
  };

  facePosition(faceLandmarks) {
    const foreheadIndex = 10;
    const chinIndex = 152;

    this.faceDirection.chin = faceLandmarks[chinIndex].y;
    this.faceDirection.forehead = faceLandmarks[foreheadIndex].y;

    // const options = {
    //   color: 'red', // Dot color
    //   radius: 10,   // Dot size
    // };
    // if (faceLandmarks[foreheadIndex]) {
    //   this.drawingUtils.drawLandmarks([faceLandmarks[foreheadIndex]], options);
    // }
    // if (faceLandmarks[chinIndex]) {
    //   this.drawingUtils.drawLandmarks([faceLandmarks[chinIndex]], options);
    // }
  }

  drawImage(side: string) {
    const canvasWidth = this.output_ctx.canvas.width;
    const canvasHeight = this.output_ctx.canvas.height;

    const imgWidth = this.demo[side].width;
    const imgHeight = this.demo[side].height;

    const imgAspectRatio = imgWidth / imgHeight;
    const imageWidth = canvasHeight * imgAspectRatio;
    const centerX = (canvasWidth - imageWidth) / 2;

    this.output_ctx.drawImage(
      this.demo[side],
      centerX,
      0,
      imageWidth,
      canvasHeight
    );
  }

  private loopToken = 0;
  predictWebcam = async (token = this.loopToken) => {
    // A re-init started a new loop; this one must stop.
    if (token !== this.loopToken) return;
    this.lastLoopAt = performance.now();
    try {
      await this.predictFrame(token);
    } catch (e) {
      // One bad frame must never kill the loop (it used to: an exception here
      // meant no more rAF and a frozen gate).
      this.reporter.report(e, 'camera');
    }
    if (token !== this.loopToken) return;
    window.requestAnimationFrame(() => this.predictWebcam(token));
  };

  private predictFrame = async (token: number) => {
    // Skip the video detection while a static upload is being detected (the
    // shared landmarker is temporarily switched to IMAGE mode).
    if (this.isDetectingImage) {
      return;
    }

    let startTimeMs = performance.now();
    let results: FaceLandmarkerResult;
    const videoEl = this.video.nativeElement as HTMLVideoElement;
    // Never feed the landmarker an unready frame (0x0 after a stream swap, or
    // readyState < 2): the wasm graph errors on it and its NEXT call can
    // deadlock the renderer main thread (seen on a Galaxy S25 Ultra after an
    // app switch: page hung, no watchdog could run). Same for a graph that is
    // being rebuilt, and timestamps must be strictly increasing.
    const videoReady =
      videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0;
    if (
      videoReady &&
      !this.recovering &&
      !this.detectorPoisoned &&
      this.lastVideoTime !== videoEl.currentTime
    ) {
      this.lastVideoTime = videoEl.currentTime;
      try {
        const src = this.detectionSource(videoEl);
        const ts = Math.max(startTimeMs, this.lastDetectTs + 1);
        this.lastDetectTs = ts;
        const t0 = performance.now();
        results = this.faceLandmarker.detectForVideo(src, ts);
        this.perfAdd('detect', performance.now() - t0);
      } catch (e) {
        // A throw leaves the graph in an error state; calling it again is
        // what hangs. Mark it poisoned and rebuild before any further call.
        results = undefined;
        this.detectorPoisoned = true;
        this.reporter.logEvent('camera.detect.throw', {
          msg: String((e as any)?.message || e).slice(0, 120),
        });
        this.recoverDetector('nan');
      }
    }

    if (results) {
      await this.setupWebcam();

      const raw = results?.faceLandmarks?.[0];
      this.poseMatrix = (results as any)?.facialTransformationMatrixes?.[0]?.data || null;
      const finite = landmarksFinite(raw);
      if (raw && !finite) this.onNanLandmarks();
      this.faceFoundLandMarks = finite ? raw : undefined;

      const nowMs = performance.now();
      if (finite) {
        this.faceEverSeen = true;
        this.lastFaceAt = nowMs;
        this.stallRecoveries = 0;
      } else if (
        (this.faceEverSeen && nowMs - this.lastFaceAt > this.DETECTOR_STALL_MS) ||
        (!this.faceEverSeen &&
          this.loadedAt &&
          nowMs - this.loadedAt > this.NEVER_SEEN_MS)
      ) {
        this.recoverDetector('stalled');
      }

      // The target oval + cross is part of the frame, face or no face.
      if (!this.faceFoundLandMarks) this.axes = null;
      this.drawTarget();

      if (this.faceFoundLandMarks) {
        const landmarks = this.faceFoundLandMarks;

        this.countBlinks(results);
        this.updateFaceDirection();
        this.facePosition(landmarks);

        await this.captureImageOnOrientation();

        // Guides go green on POSITION (the ovals overlap); the countdown text
        // separately handles hold/movement.
        const valid = this.wantFullGrab;
        const lineColor =
          this.error?.type === MessageType.COUNTDOWN ? '#4CAF50' : '#ffffff';


        // Alignment guides: a steady target oval (where the face must be) and
        // a soft oval hugging the detected face; put one on the other.
        this.drawFaceGuide(landmarks);

        // Contours only. The full 2600-line tesselation was the single most
        // expensive draw per frame on phones and adds nothing to alignment.
        const lw = this.cssPx(1);
        // (no FACE_OVAL contour: the face guide oval replaces it)
        for (const set of [
          FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE,
          FaceLandmarker.FACE_LANDMARKS_RIGHT_EYEBROW,
          FaceLandmarker.FACE_LANDMARKS_LEFT_EYE,
          FaceLandmarker.FACE_LANDMARKS_LEFT_EYEBROW,
          FaceLandmarker.FACE_LANDMARKS_LIPS,
          FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS,
          FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS,
        ]) {
          this.drawingUtils.drawConnectors(landmarks, set, {
            color: 'rgba(255,255,255,0.38)',
            lineWidth: lw,
          });
        }
      }
      this.drawHoldProgress();
      this.drawImage('cover');
      this.logLayoutOnce(!!results.faceLandmarks[0]);
      this.perfAdd('frame', performance.now() - startTimeMs);
      if (this.faceFoundLandMarks && this.pose3d) {
        // Paired samples (legacy tilt metric vs 3D pose) every ~2s, shipped
        // with camera.perf, to calibrate the gate against the real pose.
        const nowP = performance.now();
        if (nowP - this.lastPoseSampleAt > 2000) {
          this.lastPoseSampleAt = nowP;
          this.poseSamples.push([
            this.faceDirection.roll || 0,
            this.faceDirection.yaw || 0,
            this.pose3d.pitch,
            this.pose3d.yaw,
            this.pose3d.roll,
          ]);
          if (this.poseSamples.length > 6) this.poseSamples.shift();
        }
      }
      this.perfFlush();
    }
  };

  // Copy the current video frame once. While the gate is red a small grab is
  // enough (detection only); while green the grab is sized so the candidate
  // crop keeps >= CANDIDATE_MAX_SHORT_SIDE px (never more than the video).
  private grabFrame(video: HTMLVideoElement): HTMLCanvasElement | null {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const longSide = Math.max(vw, vh);
    if (!longSide) return null;
    let maxSide = this.DETECT_MAX_SIDE;
    if (this.wantFullGrab) {
      const need =
        this.lastCropSw > 0
          ? (this.CANDIDATE_MAX_SHORT_SIDE / this.lastCropSw) * longSide
          : longSide;
      // Quantize (256px steps) so the grab canvas is not re-allocated every
      // frame as the face size jitters.
      const q = Math.ceil(need / 256) * 256;
      maxSide = Math.min(longSide, Math.max(this.DETECT_MAX_SIDE, q));
    }
    const scale = Math.min(1, maxSide / longSide);
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));
    if (!this.frameCanvas) {
      this.frameCanvas = document.createElement('canvas');
      this.frameCtx = this.frameCanvas.getContext('2d');
    }
    if (!this.frameCtx) return null;
    if (this.frameCanvas.width !== w || this.frameCanvas.height !== h) {
      this.frameCanvas.width = w;
      this.frameCanvas.height = h;
    }
    const t0 = performance.now();
    this.frameCtx.drawImage(video, 0, 0, w, h);
    this.perfAdd(this.wantFullGrab ? 'grabFull' : 'grabSmall', performance.now() - t0);
    this.frameScale = scale;
    this.frameIsFull = this.wantFullGrab;
    return this.frameCanvas;
  }

  // The frame the landmarker sees: the grab itself when it is small, else a
  // DETECT_MAX_SIDE-bounded copy of it (canvas->canvas, cheap).
  private detectionSource(video: HTMLVideoElement): HTMLVideoElement | HTMLCanvasElement {
    const grab = this.grabFrame(video);
    if (!grab) return video;
    const longSide = Math.max(grab.width, grab.height);
    if (longSide <= this.DETECT_MAX_SIDE) return grab;
    const scale = this.DETECT_MAX_SIDE / longSide;
    const w = Math.max(1, Math.round(grab.width * scale));
    const h = Math.max(1, Math.round(grab.height * scale));
    if (!this.detectCanvas) {
      this.detectCanvas = document.createElement('canvas');
      this.detectCtx = this.detectCanvas.getContext('2d');
    }
    if (!this.detectCtx) return grab;
    if (this.detectCanvas.width !== w || this.detectCanvas.height !== h) {
      this.detectCanvas.width = w;
      this.detectCanvas.height = h;
    }
    this.detectCtx.drawImage(grab, 0, 0, w, h);
    return this.detectCanvas;
  }

  // Throw the current landmarker away and create a fresh one on the same
  // delegate (used when the graph is in an error state).
  private async rebuildLandmarker() {
    const delegate = CameraComponent.landmarkerDelegate;
    const old = CameraComponent.faceLandmarker;
    CameraComponent.faceLandmarker = null;
    try {
      old?.close();
    } catch {
      // best effort
    }
    await this.createFaceLandmarker(delegate);
    await this.warmUpLandmarker();
  }

  // First GPU inference compiles shaders (seconds on some phones). Do it while
  // the loader is up, on a blank frame, so the camera appears WITH its mesh.
  private async warmUpLandmarker() {
    if (CameraComponent.warmedUp) return;
    const t0 = performance.now();
    try {
      const c = document.createElement('canvas');
      c.width = this.DETECT_MAX_SIDE;
      c.height = this.DETECT_MAX_SIDE;
      const ctx = c.getContext('2d');
      ctx?.fillRect(0, 0, c.width, c.height);
      for (let i = 0; i < 2; i++) {
        this.faceLandmarker.detectForVideo(c, performance.now());
        await new Promise((r) => setTimeout(r, 0));
      }
      c.width = 0;
      c.height = 0;
      CameraComponent.warmedUp = true;
    } catch (e) {
      // warm-up is best effort; the live loop tolerates a cold graph
    }
    this.reporter.logEvent('camera.landmarker.warmup', {
      ms: Math.round(performance.now() - t0),
      delegate: CameraComponent.landmarkerDelegate,
    });
  }

  // Canvas px for a size given in CSS px (the overlay is scaled to its box).
  private cssPx(px: number): number {
    const canvas = this.output_canvas.nativeElement as HTMLCanvasElement;
    const rectW = canvas.getBoundingClientRect().width || canvas.width;
    return (px * canvas.width) / rectW;
  }

  // Steady target oval (derived from the position box the gate enforces) +
  // a moving oval fitted to the face; 30% opacity, soft glow, ~2.5 CSS px.
  private faceOvalIdx: number[] | null = null;
  // Shared style for both guides: red until the position is accepted, then
  // green; "fuzzy" = a wide faint stroke under a crisp one (canvas shadowBlur
  // was an offscreen blur of the whole overlay per stroke: 2 fps on Android).
  // Production look: thin luminous strokes (a soft halo under a crisp core),
  // rounded caps, calm colours: soft coral while an axis is off, mint when it
  // is right, the brand pink for progress. The halo "breathes" once every
  // axis is green.
  private readonly GUIDE_OK = '#7BE3A9';
  private readonly GUIDE_OFF = '#FF8FA3';
  private guideStroke(ctx: CanvasRenderingContext2D, k = 1, alpha = 1) {
    const lw = this.cssPx(3) * k;
    const breathe = this.allAxesOk() ? 0.08 * (1 + Math.sin(performance.now() / 350)) : 0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = (0.14 + breathe) * alpha;
    ctx.lineWidth = lw * 3.2;
    ctx.stroke();
    ctx.globalAlpha = 0.95 * alpha;
    ctx.lineWidth = lw;
    ctx.stroke();
  }
  private allAxesOk(): boolean {
    const a = this.axes;
    return !!a && a.centered && a.box && a.tilt && a.turn;
  }
  private brandColour(): string {
    if (!this.brandColourCache) {
      const v = getComputedStyle(document.body).getPropertyValue('--brand-color').trim();
      this.brandColourCache = v || '#ff0083';
    }
    return this.brandColourCache;
  }
  private brandColourCache = '';
  private targetGeom() {
    const ctx = this.output_ctx as CanvasRenderingContext2D;
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    // Box mid-points (see FaceStabilizationService.isPositionValid).
    const top = this.relaxedFraming ? 0.26 : 0.28;
    const bottom = this.relaxedFraming ? 0.835 : 0.82;
    const faceH = (bottom - top) * H; // forehead-skin -> chin at the ideal spot
    // Visual only: the ovals enclose the whole head (hairline included), not
    // just forehead-skin-to-chin, so they grow upward and a little sideways.
    // Same growth on the face oval below, so overlap still means "correct".
    const ry = (faceH / 2) * this.GUIDE_GROW_Y;
    const tcy = ((top + bottom) / 2) * H - faceH * this.GUIDE_UP;
    return { W, H, tcx: W / 2, tcy, rx: (faceH / 2) * 0.74 * this.GUIDE_GROW_X, ry };
  }
  private readonly GUIDE_GROW_Y = 1.22;
  private readonly GUIDE_GROW_X = 1.08;
  private readonly GUIDE_UP = 0.11; // fraction of face height, shifts the oval up

  // Steady target: fixed centre, fixed size (box height, 0.74 aspect), a
  // straight cross through its centre. Drawn on EVERY frame, face or not.
  // Per-axis gate state for the guide colours (null = no face): the oval
  // answers distance/height, the vertical line left/right + turn, the
  // horizontal line tilt. Each element is green on its own condition, on both
  // the target and the face guide; all green == the gate's green.
  private axes: { centered: boolean; box: boolean; tilt: boolean; turn: boolean } | null = null;
  private guideColours() {
    const a = this.axes;
    const col = (ok: boolean) => (ok ? this.GUIDE_OK : this.GUIDE_OFF);
    return {
      oval: col(!!a && a.box),
      vertical: col(!!a && a.centered && a.turn),
      horizontal: col(!!a && a.tilt),
    };
  }

  private drawTarget() {
    const ctx = this.output_ctx as CanvasRenderingContext2D;
    const { tcx, tcy, rx, ry } = this.targetGeom();
    const c = this.guideColours();
    const gap = this.cssPx(14); // the reticle leaves the centre open
    ctx.save();

    // oval: fine dashed viewfinder ring
    ctx.setLineDash([this.cssPx(6), this.cssPx(7)]);
    ctx.strokeStyle = c.oval;
    ctx.beginPath();
    ctx.ellipse(tcx, tcy, rx, ry, 0, 0, Math.PI * 2);
    this.guideStroke(ctx, 0.9, 0.85);
    ctx.setLineDash([]);

    // reticle: dashed hairlines that stop short of the centre
    ctx.setLineDash([this.cssPx(6), this.cssPx(7)]);
    ctx.strokeStyle = c.vertical;
    ctx.beginPath();
    ctx.moveTo(tcx, tcy - ry);
    ctx.lineTo(tcx, tcy - gap);
    ctx.moveTo(tcx, tcy + gap);
    ctx.lineTo(tcx, tcy + ry);
    this.guideStroke(ctx, 0.6, 0.85);
    ctx.strokeStyle = c.horizontal;
    ctx.beginPath();
    ctx.moveTo(tcx - rx, tcy);
    ctx.lineTo(tcx - gap, tcy);
    ctx.moveTo(tcx + gap, tcy);
    ctx.lineTo(tcx + rx, tcy);
    this.guideStroke(ctx, 0.6, 0.85);
    ctx.setLineDash([]);

    ctx.restore();
  }

  // Hold progress: the brand pink fills the target ring clockwise from the
  // top over the 5s hold (this IS the countdown). Drawn last, above the face
  // guide, so it is never hidden behind the moving oval.
  private drawHoldProgress() {
    if (!this.faceStabilization.isHolding()) return;
    const ctx = this.output_ctx as CanvasRenderingContext2D;
    const { tcx, tcy, rx, ry } = this.targetGeom();
    const f = Math.min(
      1,
      this.faceStabilization.getHoldElapsedMs() / this.faceStabilization.getHoldRequiredMs()
    );
    ctx.save();
    ctx.strokeStyle = this.brandColour();
    ctx.beginPath();
    ctx.ellipse(tcx, tcy, rx, ry, 0, -Math.PI / 2, -Math.PI / 2 + f * Math.PI * 2);
    this.guideStroke(ctx, 1.6);
    ctx.restore();
  }

  // Her head: an oval around the face rotated with the eye line, plus a rigid
  // cross that moves with the head like on a sculptor's sphere: the midline
  // follows the landmarks (bends with a turn) and the horizontal bar shifts
  // up/down with the pitch (chin up -> bar up), so line-on-line with the
  // target's cross means centred, level AND not tilted.
  private drawFaceGuide(landmarks: { x: number; y: number }[]) {
    const c = this.guideColours();
    const ctx = this.output_ctx as CanvasRenderingContext2D;
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    if (!this.faceOvalIdx) {
      const set = new Set<number>();
      for (const c of FaceLandmarker.FACE_LANDMARKS_FACE_OVAL) {
        set.add(c.start);
        set.add(c.end);
      }
      this.faceOvalIdx = [...set];
    }
    let minX = 1, maxX = 0, minY = 1, maxY = 0;
    for (const i of this.faceOvalIdx) {
      const p = landmarks[i];
      if (!p) continue;
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const le = landmarks[33];
    const re = landmarks[263];
    const rot = le && re ? Math.atan2((re.y - le.y) * H, (re.x - le.x) * W) : 0;
    // Same visual growth as the target (see targetGeom); the upward shift
    // runs along the head's own axis.
    const faceH = (maxY - minY) * H;
    const up = faceH * this.GUIDE_UP;
    const fcx = ((minX + maxX) / 2) * W + Math.sin(rot) * up;
    const fcy = ((minY + maxY) / 2) * H - Math.cos(rot) * up;
    const frx = ((maxX - minX) / 2) * W * this.GUIDE_GROW_X;
    const fry = (faceH / 2) * this.GUIDE_GROW_Y;

    ctx.save();
    ctx.strokeStyle = c.oval;
    ctx.beginPath();
    ctx.ellipse(fcx, fcy, frx, fry, rot, 0, Math.PI * 2);
    this.guideStroke(ctx);

    // midline: oval top -> forehead -> nose bridge -> nose -> chin -> oval
    // bottom (the landmarks stop at the forehead skin; the oval reaches the
    // hairline, so the line is extended along the head's axis to meet it).
    ctx.strokeStyle = c.vertical;
    ctx.beginPath();
    ctx.moveTo(fcx + Math.sin(rot) * fry, fcy - Math.cos(rot) * fry);
    for (const i of this.MIDLINE_IDX) {
      const p = landmarks[i];
      if (p) ctx.lineTo(p.x * W, p.y * H);
    }
    ctx.lineTo(fcx - Math.sin(rot) * fry, fcy + Math.cos(rot) * fry);
    this.guideStroke(ctx, 0.7);

    // horizontal line = the sphere's equator: straight through the centre
    // when the pitch is 0, bowing up when the chin is up and down when the
    // chin is down (the exact value the gate judges, so straight == green).
    // Endpoints stay on the oval; the bow at the midline is R*sin(pitch).
    const pitchDeg = this.gatePitchDeg();
    const bow = -Math.sin((pitchDeg * Math.PI) / 180) * fry;
    ctx.strokeStyle = c.horizontal;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    const x0 = fcx - cosR * frx, y0 = fcy - sinR * frx;
    const x1 = fcx + cosR * frx, y1 = fcy + sinR * frx;
    // quadratic control point = centre + 2*bow along the oval's vertical axis
    const cxp = fcx - sinR * (2 * bow);
    const cyp = fcy + cosR * (2 * bow);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(cxp, cyp, x1, y1);
    this.guideStroke(ctx, 0.7);
    ctx.restore();
  }

  // The pitch the gate judges, in degrees, + = chin up (isPositionValid says
  // "tilt chin down" above +10). Single source for the guide and the gate.
  private gatePitchDeg(): number {
    return this.faceDirection.roll || 0;
  }

  private readonly MIDLINE_IDX = [10, 151, 9, 8, 168, 6, 197, 195, 5, 4, 1, 2, 164, 0, 17, 18, 200, 199, 175, 152];

  private onNanLandmarks() {
    this.nanFrames++;
    this.reporter.logEvent('camera.landmarks.nan', {
      count: this.nanFrames,
      delegate: CameraComponent.landmarkerDelegate,
    });
    this.recoverDetector('nan');
  }

  // Reset the landmarker so a poisoned tracking state (NaN frame, lost face)
  // does not freeze the gate forever. Repeated NaN frames on the GPU delegate
  // move the whole session to the CPU delegate. Detection is paused while the
  // graph is rebuilt; the loop simply skips those frames.
  private recoverDetector(reason: 'nan' | 'stalled') {
    const now = performance.now();
    // Stalls back off (4s, 8s, 16s...) so an empty room does not rebuild the
    // graph every few seconds; a NaN frame is acted on at once.
    const cooldown =
      reason === 'stalled'
        ? this.RECOVERY_COOLDOWN_MS * Math.pow(2, Math.min(this.stallRecoveries, 4))
        : 0;
    if (this.recovering || now - this.lastRecoveryAt < cooldown) {
      return;
    }
    // A CPU graph that threw is rebuilt from scratch (setOptions on an
    // errored graph is not reliable); GPU graphs go to CPU as before.
    const rebuild = this.detectorPoisoned;
    this.recovering = true;
    this.lastRecoveryAt = now;
    this.lastFaceAt = now; // give the fresh graph a full stall window
    this.loadedAt = now;
    if (reason === 'stalled') this.stallRecoveries++;
    // GPU -> CPU on the first NaN frame, or when the GPU graph never yields a
    // face at all (second stall in a row before any face was tracked).
    // GPU -> CPU on the first NaN frame, on a graph THROW (seen on prod: the
    // GPU graph fails CalculatorGraph::Run() on every call on a Galaxy S25
    // Ultra, and rebuilding it on GPU again just loops), or when the GPU graph
    // never yields a face (second stall in a row).
    const toCpu =
      CameraComponent.landmarkerDelegate === 'GPU' &&
      (this.nanFrames >= this.NAN_CPU_FALLBACK_AFTER ||
        this.detectorPoisoned ||
        (reason === 'stalled' && this.stallRecoveries >= 2));
    if (toCpu) CameraComponent.rememberDelegate('CPU');
    this.reporter.logEvent('camera.detector.recover', {
      reason,
      toCpu,
      nanFrames: this.nanFrames,
    });
    this.isDetectingImage = true;
    (async () => {
      try {
        if (toCpu) {
          await this.createFaceLandmarker('CPU');
        } else if (rebuild) {
          await this.rebuildLandmarker();
        } else {
          // setOptions rebuilds the graph (fresh tracking state) in place.
          await this.faceLandmarker.setOptions({ runningMode: 'VIDEO' });
        }
        this.detectorPoisoned = false;
        this.lastDetectTs = 0;
      } catch (e) {
        this.reporter.report(e, 'camera');
      } finally {
        this.isDetectingImage = false;
        this.recovering = false;
      }
    })();
  }

  // One-shot geometry report after the first drawn frame: when someone says
  // "I don't see the mesh" on a device we cannot attach to, this tells us
  // whether the overlay canvas actually sits on the video and at what size.
  private layoutLogged = false;
  private logLayoutOnce(faceDrawn: boolean) {
    if (this.layoutLogged || !faceDrawn) return;
    this.layoutLogged = true;
    try {
      const rect = (el: Element | null) => {
        const r = el?.getBoundingClientRect();
        return r
          ? [Math.round(r.x), Math.round(r.y), Math.round(r.width), Math.round(r.height)]
          : null;
      };
      const video = this.video.nativeElement as HTMLVideoElement;
      const canvas = this.output_canvas.nativeElement as HTMLCanvasElement;
      const cs = getComputedStyle(canvas);
      this.reporter.logEvent('camera.layout', {
        vw: window.innerWidth,
        vh: window.innerHeight,
        dpr: window.devicePixelRatio,
        video: [video.videoWidth, video.videoHeight],
        videoRect: rect(video),
        canvas: [canvas.width, canvas.height],
        canvasRect: rect(canvas),
        canvasCss: [cs.display, cs.visibility, cs.opacity, cs.zIndex],
        cover: [this.demo.cover.naturalWidth, this.demo.cover.naturalHeight],
        relaxed: this.relaxedFraming,
      });
    } catch (e) {
      // diagnostics only
    }
  }

  countBlinks = (faceLandmarkerResult) => {
    if (!this.faceFoundLandMarks) {
      return;
    }

    this.eyeBlinkLeft = faceLandmarkerResult.faceBlendshapes[0].categories.find(
      (shape) => shape.categoryName === 'eyeBlinkLeft'
    ).score;
    this.eyeBlinkRight =
      faceLandmarkerResult.faceBlendshapes[0].categories.find(
        (shape) => shape.categoryName === 'eyeBlinkRight'
      ).score;

    const blinkThreshold = 0.5;

    if (
      this.eyeBlinkLeft > blinkThreshold &&
      this.eyeBlinkRight > blinkThreshold
    ) {
      if (!this.eyeClosed) {
        this.blinkCounter++;
        this.eyeClosed = true;
      }
    } else {
      this.eyeClosed = false;
    }
  };

  // Head pose from MediaPipe's facial transformation matrix (4x4, column
  // major, canonical face -> camera). ZYX Euler angles in degrees. Used for
  // the guides + logged next to the legacy nose-depth metric for calibration.
  private poseMatrix: number[] | Float32Array | null = null;
  pose3d: { pitch: number; yaw: number; roll: number } | null = null;
  private computePose3d() {
    const m = this.poseMatrix;
    if (!m || m.length < 16) {
      this.pose3d = null;
      return;
    }
    // R[row][col] with column-major storage: m[col*4 + row]
    const r = (row: number, col: number) => m[col * 4 + row];
    const deg = 180 / Math.PI;
    const pitch = Math.atan2(r(2, 1), r(2, 2)) * deg;
    const yaw = Math.atan2(-r(2, 0), Math.hypot(r(2, 1), r(2, 2))) * deg;
    const roll = Math.atan2(r(1, 0), r(0, 0)) * deg;
    this.pose3d = { pitch: Math.round(pitch * 10) / 10, yaw: Math.round(yaw * 10) / 10, roll: Math.round(roll * 10) / 10 };
  }

  updateFaceDirection() {
    this.computePose3d();
    const noseTip = this.faceFoundLandMarks[1];
    const leftEye = this.faceFoundLandMarks[33];
    const rightEye = this.faceFoundLandMarks[263];

    const dx = rightEye.x - leftEye.x;
    const dy = rightEye.y - leftEye.y;
    const dz = rightEye.z - leftEye.z;

    // Pitch: Vertical angle (up-down)
    const pitch =
      Math.atan2(dz, Math.sqrt(dx * dx + dy * dy)) * (180 / Math.PI);

    // Yaw: Horizontal angle (left-right)
    const yaw = Math.atan2(dy, dx) * (180 / Math.PI);

    // Roll: Side Tilt
    const eyeMidpoint = {
      x: (leftEye.x + rightEye.x) / 2,
      y: (leftEye.y + rightEye.y) / 2,
      z: (leftEye.z + rightEye.z) / 2,
    };
    let roll =
      Math.atan2(noseTip.z - eyeMidpoint.z, noseTip.y - eyeMidpoint.y) *
        (180 / Math.PI) +
      45;

    this.faceDirection = {
      pitch: Math.round(-pitch),
      yaw: Math.round(-yaw),
      roll: Math.round(-roll),
    };
  }

  // Compute the normalization crop rect (in source pixels) from face landmarks:
  // centered on the face, fixed NORM aspect, face at NORM_FACE_WIDTH_FRAC width.
  // `sw` (short side, portrait) is the native pixel width of the crop and is the
  // only signal the live gate needs ("is the face big enough?"). Returns null if
  // no face is detected.
  computeFaceCrop(
    frameW: number,
    frameH: number,
    landmarks: any = this.faceFoundLandMarks
  ): {
    sx: number;
    sy: number;
    sw: number;
    sh: number;
    tooClose: boolean;
    cameraCapable: boolean;
  } | null {
    const lm = landmarks;
    if (!lm?.length || !frameW || !frameH) {
      return null;
    }

    const xs = lm.map((p: { x: number }) => p.x);
    const ys = lm.map((p: { y: number }) => p.y);
    const minX = Math.min(...xs) * frameW;
    const maxX = Math.max(...xs) * frameW;
    const minY = Math.min(...ys) * frameH;
    const maxY = Math.max(...ys) * frameH;

    const faceW = maxX - minX;
    const faceH = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // Face nearly fills the frame on either axis -> can't keep margin, edges
    // would be cropped -> too close.
    const tooClose =
      faceW > this.NORM_FACE_FILL_MAX * frameW ||
      faceH > this.NORM_FACE_FILL_MAX * frameH;

    const aspect = this.NORM_ASPECT_W / this.NORM_ASPECT_H; // width / height (<1)

    // Largest portrait crop this frame can yield (full-frame fit). If even that
    // is below the HD minimum, the camera is too low-res regardless of framing.
    const maxCropShort = Math.min(frameW, frameH * aspect);
    const cameraCapable = maxCropShort >= this.effMinNativeShortSide;

    // Crop large enough that the face fits BOTH the width and height targets.
    let cropW = Math.max(
      faceW / this.NORM_FACE_WIDTH_FRAC,
      (faceH / this.NORM_FACE_HEIGHT_FRAC) * aspect
    );
    // Non-finite landmarks would make a NaN crop (which then sails through
    // every `<` check and yields a 0px canvas). Treat it as "no face".
    if (!Number.isFinite(cropW) || cropW <= 0) {
      return null;
    }
    let cropH = cropW / aspect;

    // Clamp to the frame while preserving aspect (limits how much margin we can
    // keep when the face is large/close — face just ends up a bit bigger, still
    // valid for the API).
    if (cropW > frameW) {
      cropW = frameW;
      cropH = cropW / aspect;
    }
    if (cropH > frameH) {
      cropH = frameH;
      cropW = cropH * aspect;
    }

    // Center on the face, kept fully inside the frame.
    let sx = cx - cropW / 2;
    let sy = cy - cropH / 2;
    sx = Math.max(0, Math.min(sx, frameW - cropW));
    sy = Math.max(0, Math.min(sy, frameH - cropH));

    return { sx, sy, sw: cropW, sh: cropH, tooClose, cameraCapable };
  }

  // Draw the crop rect into a fixed-aspect output canvas at the highest
  // resolution the crop allows within the API's short/long-side limits. This is
  // the exact image we upload. `mirror` matches the front-camera preview (live
  // capture); uploaded files are NOT mirrored.
  buildNormalizedCanvas(
    source: CanvasImageSource,
    crop: { sx: number; sy: number; sw: number; sh: number },
    mirror = true,
    target?: HTMLCanvasElement,
    maxShortSide = this.NORM_MAX_SHORT_SIDE
  ): HTMLCanvasElement {
    // Never upscale: cap at maxShortSide, otherwise use native crop px.
    // (The gate guarantees crop.sw >= NORM_MIN_NATIVE_SHORT_SIDE before we get here.)
    const outW = Math.round(Math.min(crop.sw, maxShortSide));
    const outH = Math.round((outW * this.NORM_ASPECT_H) / this.NORM_ASPECT_W);
    if (!(outW > 0 && outH > 0)) {
      throw new Error(`invalid crop ${crop.sw}x${crop.sh}`);
    }

    // `target` lets the still-frame selector reuse pooled canvases (one draw
    // per detection frame during the hold, no per-frame allocation).
    const canvas = target || document.createElement('canvas');
    if (canvas.width !== outW || canvas.height !== outH) {
      canvas.width = outW;
      canvas.height = outH;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, outW, outH);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (mirror) {
      ctx.translate(outW, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(source, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, outW, outH);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return canvas;
  }

  // Lighting changes slowly: sample it a few times a second, not per frame
  // (a video->canvas draw + readback each frame was a measurable share of the
  // budget on phones), on one reusable canvas.
  private brightnessCanvas: HTMLCanvasElement | null = null;
  private brightnessAt = 0;
  private brightnessOk = true;
  private readonly BRIGHTNESS_INTERVAL_MS = 300;
  videoBrightnessOk(): boolean {
    const now = performance.now();
    if (now - this.brightnessAt < this.BRIGHTNESS_INTERVAL_MS) return this.brightnessOk;
    this.brightnessAt = now;
    this.brightnessOk = this.checkVideoBrightness();
    return this.brightnessOk;
  }

  checkVideoBrightness(): boolean {
    const video: CanvasImageSource = this.frameCanvas || this.video.nativeElement;
    if (!this.brightnessCanvas) {
      this.brightnessCanvas = document.createElement('canvas');
      this.brightnessCanvas.width = 320;
      this.brightnessCanvas.height = 240;
    }
    const tempCanvas = this.brightnessCanvas;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
    if (!tempCtx) return true;
    tempCtx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
    return this.checkImageBrightness(tempCanvas);
  }

  checkImageBrightness(canvas: HTMLCanvasElement): boolean {
    if (!(canvas.width > 0 && canvas.height > 0)) {
      this.reporter.logEvent('camera.brightness.emptyCanvas');
      return false;
    }
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let totalBrightness = 0;
    let pixelCount = 0;

    // Sample every 10th pixel for performance (still gives accurate brightness reading)
    for (let i = 0; i < data.length; i += 40) {
      // RGBA = 4 bytes per pixel, so i += 40 samples every 10th pixel
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // Calculate perceived brightness using luminance formula
      const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
      totalBrightness += brightness;
      pixelCount++;
    }

    const averageBrightness = totalBrightness / pixelCount;
    return averageBrightness >= this.effMinBrightness;
  }

  capture(ctx) {
    return new Promise((resolve, reject) => {
      ctx.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Blob creation failed'));
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }

  // Sample the current (position-valid) frame into the still-frame selector:
  // measure eye-patch sharpness on the live video, and hand over a draw
  // callback that renders the normalized upload crop for THIS frame's
  // landmarks. The selector only calls draw() for eligible (non-blink) frames.
  private sampleFrameForCapture(crop: {
    sx: number;
    sy: number;
    sw: number;
    sh: number;
  }) {
    if (this.capturedImages?.front) return;
    const video = this.video.nativeElement;
    const frameW = video.videoWidth;
    const frameH = video.videoHeight;
    const landmarks = this.faceFoundLandMarks;
    if (!landmarks?.length || !frameW || !frameH) return;
    // The first green frame only had the small (detection) grab; the next one
    // is grabbed full and gets sampled. No extra copy from the video, ever.
    const grab = this.frameCanvas;
    if (!grab || !this.frameIsFull) return;
    const now = performance.now();
    if (now - this.lastSampleAt < this.SAMPLE_MIN_INTERVAL_MS) return;
    this.lastSampleAt = now;

    const blink =
      this.eyeBlinkLeft > this.BLINK_THRESHOLD ||
      this.eyeBlinkRight > this.BLINK_THRESHOLD;
    const k = this.frameScale;
    const gCrop = { sx: crop.sx * k, sy: crop.sy * k, sw: crop.sw * k, sh: crop.sh * k };

    try {
      const t0 = performance.now();
      this.frameSelector.push({
        t: now,
        landmarks,
        frameW,
        frameH,
        blink,
        sharpness: blink ? 0 : this.measureEyeSharpness(grab, grab.width, grab.height),
        draw: (target) =>
          this.buildNormalizedCanvas(
            grab,
            gCrop,
            true,
            target,
            this.CANDIDATE_MAX_SHORT_SIDE
          ),
      });
      this.perfAdd('sample', performance.now() - t0);
    } catch (e) {
      // Sampling must never break the live gate; the plain capture path below
      // still works without candidates.
      console.warn('CameraComponent: frame sampling failed', e);
    }
  }

  // Laplacian variance over a native-resolution patch around each eye
  // (averaged). Eyes carry the highest-contrast detail of the face, so blur
  // (motion OR autofocus hunting) shows up here first. Relative measure: only
  // compared between frames of the same hold.
  private measureEyeSharpness(
    video: CanvasImageSource,
    frameW: number,
    frameH: number
  ): number {
    const lm = this.faceFoundLandMarks;
    if (!this.sharpCanvas) {
      this.sharpCanvas = document.createElement('canvas');
      this.sharpCtx = this.sharpCanvas.getContext('2d', {
        willReadFrequently: true,
      });
    }
    const ctx = this.sharpCtx;
    if (!ctx) return 0;

    const eyes: [number, number][] = [
      [33, 133], // right eye outer/inner corners
      [362, 263], // left eye inner/outer corners
    ];
    let total = 0;
    let n = 0;
    for (const [a, b] of eyes) {
      const pa = lm[a];
      const pb = lm[b];
      if (!pa || !pb) continue;
      const cx = ((pa.x + pb.x) / 2) * frameW;
      const cy = ((pa.y + pb.y) / 2) * frameH;
      const eyeW = Math.hypot((pa.x - pb.x) * frameW, (pa.y - pb.y) * frameH);
      const pw = Math.round(eyeW * 1.6);
      const ph = Math.round(pw * 0.6);
      if (pw < 8 || ph < 8) continue;
      const sx = Math.max(0, Math.min(frameW - pw, Math.round(cx - pw / 2)));
      const sy = Math.max(0, Math.min(frameH - ph, Math.round(cy - ph / 2)));
      // Draw 1:1 (native pixels) unless the patch is huge; the scale factor is
      // constant across one hold so frames stay comparable.
      const scale = pw > this.SHARP_PATCH_MAX_W ? this.SHARP_PATCH_MAX_W / pw : 1;
      const dw = Math.round(pw * scale);
      const dh = Math.round(ph * scale);
      if (this.sharpCanvas.width !== dw || this.sharpCanvas.height !== dh) {
        this.sharpCanvas.width = dw;
        this.sharpCanvas.height = dh;
      }
      ctx.drawImage(video, sx, sy, pw, ph, 0, 0, dw, dh);
      total += laplacianVariance(ctx, dw, dh);
      n++;
    }
    return n ? total / n : 0;
  }

  captureImage = async (position: 'front') => {
    if (this.captureImageOnOrientationInProgress) {
      return;
    }
    this.captureImageOnOrientationInProgress = true;
    const video = this.video.nativeElement;
    const frameW = video.videoWidth;
    const frameH = video.videoHeight;

    // Deterministic normalization: one rule (enough native pixels), everything
    // else (aspect, centering, face size) handled by the crop math.
    const crop = this.computeFaceCrop(frameW, frameH);
    if (crop && !crop.cameraCapable) {
      console.error(
        `CameraComponent: camera too low-res for HD - frame ${frameW}x${frameH} cannot yield a ${this.NORM_MIN_NATIVE_SHORT_SIDE}px portrait crop`
      );
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.cameraResolutionTooLow'),
        0
      );
      return;
    }
    if (!crop || crop.sw < this.effMinNativeShortSide) {
      console.error(
        `CameraComponent: face crop too small for HD (native short ${
          crop ? Math.round(crop.sw) : 0
        }px < ${this.NORM_MIN_NATIVE_SHORT_SIDE}px) on frame ${frameW}x${frameH}`
      );
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.moveCloser'),
        3
      );
      return;
    }
    if (crop.tooClose) {
      console.error(
        `CameraComponent: face too close (fills > ${Math.round(
          this.NORM_FACE_FILL_MAX * 100
        )}% of frame ${frameW}x${frameH}) - would crop edges`
      );
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.moveBack'),
        3
      );
      return;
    }

    // Prefer the stillest/sharpest frame sampled during the hold; the live
    // frame is only the fallback (e.g. selector never got a full window on a
    // very slow device).
    const selStats = this.frameSelector.stats();
    let best = this.frameSelector.takeBest();
    if (best && !(best.canvas.width > 0 && best.canvas.height > 0)) {
      this.reporter.logEvent('camera.capture.badBest', { ...selStats });
      best = null;
    }
    const normalized = best
      ? best.canvas
      : this.buildNormalizedCanvas(video, crop);

    try {
      const isBrightEnough = this.checkImageBrightness(normalized);
      if (!isBrightEnough) {
        console.error(
          'CameraComponent: Captured image is too dark for analysis'
        );
        this.error = this.messageHandler.updateMessage(
          this.translationService.translate('cameraErrors.imageTooDark'),
          3
        );
        return;
      }

      // Build the blob BEFORE publishing the photo: the host hides this
      // component the moment `capturedImages.front` is set, so anything after
      // an await here would run on a destroyed component (the emit included).
      // The blob is the whole photo now: nothing displays the shot back to her,
      // so the second, PNG copy the old preview needed is not encoded at all.
      const blob = await this.capture(normalized);
      this.capturedImages[position] = {
        blob,
        forced: false,
        reason: 'ok',
        meta: {
          outW: normalized.width,
          outH: normalized.height,
          selected: !!best,
          ...selStats,
        },
      };
      console.log(
        `CameraComponent: normalized image captured ${normalized.width}x${
          normalized.height
        } (${
          best
            ? `best of ${selStats.samples} samples: motion ${selStats.bestMotion} px/ms, sharp ${selStats.bestSharpness}, ${selStats.bestAgeMs}ms ago, ${selStats.fps} fps`
            : 'live frame, no candidate'
        }; crop ${Math.round(crop.sw)}x${Math.round(
          crop.sh
        )} @ ${Math.round(crop.sx)},${Math.round(crop.sy)} from ${frameW}x${frameH})`
      );
      this.reporter.logEvent('camera.capture.ok', {
        outW: normalized.width,
        outH: normalized.height,
        selected: !!best,
        ...selStats,
      });
      // There is no review step after the shot: the host sends the photo the
      // moment it exists, so the analysis starts here rather than on a tap.
      this.imageCaptured.emit();
    } catch (error) {
      console.error('Error creating blob:', error);
      this.reporter.report(error, 'camera');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.errorCapturingImage'),
        3
      );
    } finally {
      this.captureImageOnOrientationInProgress = false;
    }
  };

  // Largest centered portrait (4:5) crop of the frame — the fallback when no
  // usable face crop exists, so a forced capture always yields an image.
  private centerCrop(frameW: number, frameH: number) {
    let sw = frameW;
    let sh = (sw * this.NORM_ASPECT_H) / this.NORM_ASPECT_W;
    if (sh > frameH) {
      sh = frameH;
      sw = (sh * this.NORM_ASPECT_W) / this.NORM_ASPECT_H;
    }
    const sx = (frameW - sw) / 2;
    const sy = (frameH - sh) / 2;
    return { sx, sy, sw, sh, tooClose: false, cameraCapable: true };
  }

  // Manual "Continue anyway" button handler.
  continueAnyway = async () => {
    await this.forceCapture('manual_continue');
  };

  // Capture the current frame regardless of the quality gate, so a customer is
  // never dead-ended. Uses the best face crop available, falling back to a
  // centered crop when the face isn't well framed. The server remediates the
  // image (upscale / crop-zoom / downscale) and records the attempt + reason.
  forceCapture = async (reason: 'manual_continue') => {
    if (this.forcing || this.capturedImages?.front) return;
    this.forcing = true;
    try {
      const video = this.video.nativeElement;
      const frameW = video.videoWidth;
      const frameH = video.videoHeight;
      if (!frameW || !frameH) return;

      let crop = this.computeFaceCrop(frameW, frameH);
      if (!crop || !crop.sw) {
        crop = this.centerCrop(frameW, frameH);
      }

      // A frame that was green at some point during this attempt beats the
      // arbitrary live frame (it passed every gate when sampled).
      const selStats = this.frameSelector.stats();
      const best = this.frameSelector.takeBest();
      const normalized = best
        ? best.canvas
        : this.buildNormalizedCanvas(video, crop);
      // Blob first, then publish — same reason as in captureImage().
      const blob = await this.capture(normalized);
      this.capturedImages.front = {
        blob,
        forced: true,
        reason,
        meta: {
          outW: normalized.width,
          outH: normalized.height,
          stuckMs: this.readySince ? Date.now() - this.readySince : null,
          lastReason: this.currentReason || null,
          selected: !!best,
          ...selStats,
        },
      };
      this.showContinueAnyway = false;
      this.reporter.logEvent('camera.capture.forced', {
        reason,
        outW: normalized.width,
        outH: normalized.height,
        lastReason: this.currentReason || null,
      });
      this.imageCaptured.emit();
    } catch (error) {
      console.error('CameraComponent: forceCapture failed', error);
      this.reporter.report(error, 'camera');
    } finally {
      this.forcing = false;
    }
  };

  ngOnDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.noFramesTimer) clearTimeout(this.noFramesTimer);
    // srcObject is null if the camera never started (e.g. no device) - guard it.
    const stream = this.video?.nativeElement?.srcObject as MediaStream | null;
    stream?.getVideoTracks()?.forEach((track) => track.stop());
    this.loaded = false;
    // Free the sampled-frame canvases (several MB each).
    this.frameSelector.dispose();
    this.sharpCanvas = null;
    this.sharpCtx = null;
  }

  // Detect face landmarks on a static image. Reuses the single shared
  // landmarker (creating a 2nd instance can deadlock the WASM/GPU backend),
  // switching it to IMAGE mode for the detect and restoring VIDEO after.
  private detectFaceLandmarksOnImage = async (
    img: HTMLImageElement
  ): Promise<any> => {
    if (!this.faceLandmarker) {
      await this.createFaceLandmarker();
    }
    this.isDetectingImage = true;
    try {
      await this.faceLandmarker.setOptions({ runningMode: 'IMAGE' });
      const result = this.faceLandmarker.detect(img);
      return result?.faceLandmarks?.[0];
    } finally {
      await this.faceLandmarker.setOptions({ runningMode: 'VIDEO' });
      this.isDetectingImage = false;
    }
  };

  async handleFileUpload(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.reporter.logEvent('camera.upload', { type: file.type });

    if (!file.type.startsWith('image/')) {
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.selectImageFile'),
        3
      );
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = async () => {
          try {
            const frameW = img.naturalWidth;
            const frameH = img.naturalHeight;

            // Same normalization as live capture: detect the face, then crop to
            // a fixed face-centered frame so the upload satisfies the skin API.
            console.log(
              `CameraComponent: upload received ${frameW}x${frameH}, detecting face...`
            );
            const landmarks = await this.detectFaceLandmarksOnImage(img);
            if (!landmarks?.length) {
              console.warn('CameraComponent: upload - no face detected');
              this.error = this.messageHandler.updateMessage(
                this.translationService.translate('cameraErrors.noFaceDetected'),
                3
              );
              return;
            }

            const crop = this.computeFaceCrop(frameW, frameH, landmarks);
            console.log(
              `CameraComponent: upload crop ${
                crop ? `${Math.round(crop.sw)}x${Math.round(crop.sh)} capable=${crop.cameraCapable} tooClose=${crop.tooClose}` : 'null'
              } (floor ${this.NORM_MIN_NATIVE_SHORT_SIDE})`
            );
            if (!crop) {
              this.error = this.messageHandler.updateMessage(
                this.translationService.translate('cameraErrors.couldNotProcessPhoto'),
                3
              );
              return;
            }
            // No resolution floor for manual upload: the crop guarantees the
            // face is ~70% width (PerfectCorp's real requirement) and the server
            // upscales the short side to 1080 if needed. Only reject if the face
            // is cut off at the edges (can't be recovered).
            if (crop.tooClose) {
              this.error = this.messageHandler.updateMessage(
                this.translationService.translate('cameraErrors.faceCutOff'),
                3
              );
              return;
            }

            // Uploaded files are not mirrored (unlike the front-camera preview).
            const normalized = this.buildNormalizedCanvas(img, crop, false);

            if (!this.checkImageBrightness(normalized)) {
              console.error(
                'CameraComponent: Uploaded image is too dark for analysis'
              );
              this.error = this.messageHandler.updateMessage(
                this.translationService.translate('cameraErrors.uploadedImageTooDark'),
                3
              );
              return;
            }

            // Blob first, then publish — same reason as in captureImage().
            const blob = await this.capture(normalized);
            this.capturedImages.front = { blob };
            console.log(
              `CameraComponent: uploaded image normalized ${normalized.width}x${normalized.height} (crop ${Math.round(
                crop.sw
              )}x${Math.round(crop.sh)} from ${frameW}x${frameH})`
            );
            this.imageCaptured.emit();
          } catch (error) {
            console.error('Error processing image:', error);
            this.reporter.report(error, 'camera');
            this.error = this.messageHandler.updateMessage(
              this.translationService.translate('cameraErrors.errorProcessingImage'),
              3
            );
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      this.reporter.report(error, 'camera');
      this.error = this.messageHandler.updateMessage(
        this.translationService.translate('cameraErrors.errorReadingFile'),
        3
      );
    }
  }
}
